<script setup lang="ts">
import { shallowRef, watch, onUnmounted } from 'vue'
import { useStore } from 'vuex'

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { LayerItem } from '@/components/layers'
import { reorderList } from './utils'

const store = useStore()

const props = defineProps<{
  layers: any[]
}>()

const items = shallowRef([...props.layers])

// Watch for external changes to layers prop (e.g., from Vuex)
watch(() => props.layers, (newLayers) => {
  items.value = [...newLayers]
})

const emit = defineEmits(['deleteLayer'])

const deleteLayer = (layerId: string) => {
  emit('deleteLayer', layerId)
}

// Monitor for drop events and reorder items
const cleanup = combine(
  monitorForElements({
    onDrop(args) {
      const { location, source } = args

      // Didn't drop on anything
      if (!location.current.dropTargets.length) return

      const itemId = source.data.id as string
      const target = location.current.dropTargets[0]
      const targetId = target.data.id as string
      const dropPosition = target.data.position as 'above' | 'below'

      if (itemId === targetId) return

      // Reorder using the simplified flat list function
      const reorderedItems = reorderList(items.value, itemId, targetId, dropPosition)
      items.value = reorderedItems

      // Update Vuex store directly to persist the order
      if (reorderedItems.length > 0) {
        const orgId = reorderedItems[0].properties?.organization_id
        if (orgId) {
          store.dispatch('layers/reorderOrgLayers', { orgId, layers: reorderedItems })
        }
      }
    },
  }),
)

// Clean up drag-and-drop listeners when component unmounts
onUnmounted(() => {
  cleanup()
})
</script>

<template>
  <div class="list-none select-none w-full bg-background text-foreground rounded-md text-sm font-medium">
    <LayerItem
      v-for="(item, idx) in items"
      :key="item.uuid"
      :item="item"
      :position="idx"
      @deleteLayer="deleteLayer"
    />
  </div>
</template>
